import React from 'react';
import { Link } from '@inertiajs/react';
import AdminLayout from '../../layouts/AdminLayout';
import Markdown from '../../components/Markdown';

type Item = {
  id: number;
  title: string;
  body: string | null;
  user_email: string | null;
  user_id: number | null;
  state: 'active' | 'trashed' | 'archived' | null;
  meta: Record<string, string | number | null>;
};

type ContentItemProps = { kind: string; item: Item };
type ContentItemComponent = React.FC<ContentItemProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const KIND_LABEL: Record<string, string> = {
  tasks: '任务',
  wiki: 'Wiki',
  worklogs: 'Worklog',
  projects: '项目',
};

const STATE_PILL: Record<string, [string, string]> = {
  active: ['活跃', 'bg-admin-okSoft text-admin-ok'],
  trashed: ['回收站', 'bg-admin-dangerSoft text-admin-danger'],
  archived: ['已归档', 'bg-admin-surface2 text-admin-muted'],
};

const ContentItem: ContentItemComponent = ({ kind, item }) => {
  const pill = item.state ? STATE_PILL[item.state] : null;

  return (
    <>
      <h1 className="mb-3 truncate text-[14.5px] font-semibold tracking-tight">
        <span className="font-normal text-admin-muted">内容巡检 · {KIND_LABEL[kind]} / </span>
        {item.title}
      </h1>

      <div className="grid grid-cols-[minmax(0,1fr)_268px] items-start gap-3">
        <div className="rounded-lg border border-admin-line bg-admin-surface">
          <div className="flex items-center border-b border-admin-line px-3.5 py-2.5 text-[12.5px] font-semibold">
            正文
            <span className="ml-auto text-[11.5px] font-normal text-admin-muted">
              渲染复用前台的 Markdown.tsx
            </span>
          </div>
          <div className="px-5 py-4">
            {item.body ? (
              // Markdown 组件内部用的是 todo-* 色板（代码块底色等）。这里不为
              // 配色 fork 一份 —— 它是全仓库唯一的 markdown 实现，复制一份
              // 两边样式当天就开始漂。轻微的色调差异是可接受的代价。
              <Markdown size="article">{item.body}</Markdown>
            ) : (
              <div className="text-[13px] text-admin-muted">这类内容没有正文。</div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-admin-line bg-admin-surface">
          <div className="border-b border-admin-line px-3.5 py-2.5 text-[12.5px] font-semibold">
            元信息
          </div>
          <div className="p-3.5">
            <dl className="grid grid-cols-[76px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12.5px]">
              <dt className="text-admin-muted">ID</dt>
              <dd className="font-mono">{item.id}</dd>
              <dt className="text-admin-muted">归属</dt>
              <dd className="break-all">
                {item.user_id ? (
                  <Link
                    href={`/admin/users/${item.user_id}`}
                    className="text-admin-accent hover:underline"
                  >
                    {item.user_email}
                  </Link>
                ) : (
                  '—'
                )}
              </dd>
              {Object.entries(item.meta).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-admin-muted">{k}</dt>
                  <dd className="break-all">{String(v)}</dd>
                </React.Fragment>
              ))}
              {pill && (
                <>
                  <dt className="text-admin-muted">状态</dt>
                  <dd>
                    <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${pill[1]}`}>
                      {pill[0]}
                    </span>
                  </dd>
                </>
              )}
            </dl>
          </div>
        </div>
      </div>

      <div className="mt-3 max-w-[94ch] rounded-md border border-dashed border-admin-line2 bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
        详情页没有<b className="font-semibold text-admin-ink2">编辑</b>与
        <b className="font-semibold text-admin-ink2">软删</b>按钮：本期只读。
        软删将来会复用已有的 <code className="font-mono">Tasks::Discard</code> /{' '}
        <code className="font-mono">WikiPages::Discard</code>，不另开第二条删除路径。
      </div>
    </>
  );
};

ContentItem.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;

export default ContentItem;
